import { CalendarOff } from 'lucide-react';
import { BoxedText } from '@/components/common/BoxedText';
import { DetailPanel } from '@/components/common/DetailPanel';
import { DetailValue } from '@/components/common/DetailValue';
import { FormList, FormListItem } from '@/components/common/FormDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateRangeWithYear, formatDisplayDateTime } from '@/utils/date';
import type { LeaveApplicationDetail } from '../types/leaveApplication.types';
import { AppliedDaysBadge } from './AppliedDaysBadge';
import { LeaveRequestTypeBadge } from './LeaveRequestTypeBadge';
import { LeaveTypeBadge } from './LeaveTypeBadge';

interface LeaveDetailsSectionProps {
  application: LeaveApplicationDetail | undefined;
}

export function LeaveDetailsSection({ application }: LeaveDetailsSectionProps) {
  const isLoading = application === undefined;

  return (
    <DetailPanel
      title="Leave Details"
      icon={CalendarOff}
      hasTintedHeader
      headerTint="dark"
      size="compact"
    >
      <FormList>
        <FormListItem label="Request Type">
          <DetailValue isLoading={isLoading}>
            {application ? <LeaveRequestTypeBadge requestType={application.requestType} /> : null}
          </DetailValue>
        </FormListItem>
        <FormListItem label="Leave Type">
          <DetailValue isLoading={isLoading}>
            {application?.leaveTypeName ? (
              <LeaveTypeBadge leaveTypeName={application.leaveTypeName} />
            ) : null}
          </DetailValue>
        </FormListItem>
        <FormListItem label="Applied Dates">
          <DetailValue isLoading={isLoading}>
            {application ? (
              <span className="flex flex-wrap items-center gap-2">
                {formatDateRangeWithYear(application.dateFrom, application.dateTo)}
                {application.appliedDays === null ? null : (
                  <AppliedDaysBadge appliedDays={application.appliedDays} />
                )}
              </span>
            ) : null}
          </DetailValue>
        </FormListItem>
        <FormListItem label="Apply Date">
          <DetailValue isLoading={isLoading}>
            {application ? formatDisplayDateTime(application.createdAt) : null}
          </DetailValue>
        </FormListItem>
        <FormListItem label="Remarks (By Employee)">
          {application ? (
            <BoxedText isExpandable>{application.remarks}</BoxedText>
          ) : (
            <Skeleton className="h-16 w-full rounded-lg" />
          )}
        </FormListItem>
      </FormList>
    </DetailPanel>
  );
}
